/* eslint-disable @nx/enforce-module-boundaries */
import { type ChangeEvent, Fragment, useEffect, useState, useSyncExternalStore } from 'react'
import { Router, useRouter } from 'next/router'
import Script from 'next/script'
import { Button, InputField, Spinner } from '@stone-payments/jade'
import BlackBird from 'shared/blackbird'
import { APP_ENV, APP_VERSION, DEPLOY_TYPE } from 'shared/envs'
import { isomorphicCookies } from 'shared/storage'

import { authenticator } from '~/domains/platform/core/authenticator/authenticator'

import { type SwhConfigStore, MicroFrontEndConfigStoreDefault } from './swh-config-context'
const isSsr = typeof window === 'undefined'

const IS_PRD = APP_ENV() === 'prod'

const microFrontEndLocalStorage = 'microFrontEndVersion'

type Fn<T> = (a: T) => unknown

const IS_PR_PREVIEW = DEPLOY_TYPE() === 'preview'

const VERSION = (IS_PR_PREVIEW ? APP_VERSION() : '').trim().normalize('NFKD')

const Url = {
  create: (path: string) => new URL(path, 'https://stone.com.br'),
  normalizeSlashes: (s: string) => s.replace(/\/+$/, '').replace(/^\/+/, '/'),
  trailingPath: (str: string) => str.replace(/\/+$/g, '/'),
  join: (baseURL: string, ...urls: string[]) =>
    Url.trailingPath(urls.reduce((acc, el) => acc.replace(/\/+$/, '') + '/' + el.replace(/^\/+/, ''), baseURL)),
  sanitizeUrl: (s: string) =>
    IS_PR_PREVIEW ? Url.normalizeSlashes(s.normalize('NFKD').replace(`/${VERSION}/`, '/')) : Url.normalizeSlashes(s)
}

export class Store<T extends object> {
  public subscribers = new Set<Fn<T>>()

  constructor(private state: T) {}

  static create = (store: Store<SwhConfigStore>, name: 'STONE_SWH' | 'STONE_MICRO_FRONTEND') => {
    if (!isSsr) {
      window[name] = store
      window.STONE_SWH = store
    }
  }

  public get = () => {
    return this.state
  }

  public set = (callback: (t: T) => T) => {
    this.state = callback(this.state)
    this.subscribers.forEach(subscriber => subscriber(this.state))
  }

  public subscribe = (instance: Fn<T>) => {
    this.subscribers.add(instance)
    return () => this.subscribers.delete(instance)
  }
}

export const MICROFRONTEND_CDN =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:9999'
    : IS_PRD
    ? 'https://swh-cdn-prd.stone.com.br'
    : 'https://swh-cdn-stg.stone.com.br'

type StoneAuthBridge = {
  getValidToken: () => Promise<string>
  refreshToken: () => Promise<string>
}

declare global {
  interface Window {
    STONE_SWH: Store<SwhConfigStore>
    STONE_MICRO_FRONTEND: Store<SwhConfigStore>
    FRONTEND_CONCIERGE: Record<string, { rerender(): void }>
    STONE_AUTH: StoneAuthBridge
  }
}

export const broadcastRerender = (name: string) => {
  if (typeof window === 'undefined') return
  setTimeout(() => {
    const concierge = window.FRONTEND_CONCIERGE?.[name]
    if (concierge && typeof concierge.rerender === 'function') concierge.rerender()
  }, 200)
}

export const useMicroFrontEndGlobalStore = () => {
  const state = useSyncExternalStore(store.subscribe, store.get, store.get)
  return [state, store] as const
}

const store = new Store(MicroFrontEndConfigStoreDefault)
Store.create(store, 'STONE_SWH')
Store.create(store, 'STONE_MICRO_FRONTEND')

// Single refresh authority for micro-frontends: they must NOT run their own refresh flow
// (competing rotation of the same single-use refreshToken cookie). Delegate to the host
// authenticator, which serializes refresh under a mutex and owns the V2 token/cookie contract.
let refreshTokenPromise: Promise<string> | null = null

function registerAuthBridge() {
  if (isSsr) return

  window.STONE_AUTH = {
    getValidToken: async () => {
      if (refreshTokenPromise) return refreshTokenPromise
      return (await authenticator.getValidTokens()).token
    },
    refreshToken: async () => {
      if (!refreshTokenPromise) {
        refreshTokenPromise = authenticator
          .revalidateSession()
          .then(({ token }) => token)
          .finally(() => {
            refreshTokenPromise = null
          })
      }
      return refreshTokenPromise
    }
  }
}
registerAuthBridge()

const VersionSelection = (props: { version: string | undefined; setVersion: (version: string) => void }) => {
  const router = useRouter()
  const [showVersionConfig, setShowVersionConfig] = useState(false)
  const [versionInput, setVersionInput] = useState<string | undefined>(props.version)
  const onChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
    setVersionInput(e.target.value)
  }

  const onSubmit = async () => {
    localStorage.setItem(microFrontEndLocalStorage, versionInput || '')
    await BlackBird.replace({ pathname: BlackBird.getPathname(), query: { ...router.query, __swh: versionInput } })
  }

  const onReset = async () => {
    localStorage.removeItem(microFrontEndLocalStorage)
    const { __swh, ...newquery } = router.query
    console.info('Remove version from url:', __swh)
    await BlackBird.replace({ pathname: BlackBird.getPathname(), query: newquery })
  }

  if (APP_ENV() === 'prod') {
    return null
  }
  return (
    <div className="absolute left-1/2 center top-2 z-max bg-white border-r-8" style={{ zIndex: '1001' }}>
      {!showVersionConfig ? (
        <div>
          <Button variant="danger-ghost" onClick={() => setShowVersionConfig(!showVersionConfig)}>
            Version: {props.version ? props.version : 'latest'}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col py-4 px-2 border-r-2 gap-jade-100">
          <span className="text-jade-150">Version: </span>
          <InputField
            placeholder="0.0.0"
            label=""
            className="text-jade"
            onChange={onChangeInput}
            value={versionInput}
          />
          <div className="flex justify-between">
            <Button variant="primary-ghost" onClick={onSubmit}>
              Apply
            </Button>
            <Button variant="primary-ghost" onClick={onReset}>
              Reset
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export const useUrlChanges = () => {
  const router = useRouter()
  const [lastUrl, setLastUrl] = useState(Url.sanitizeUrl(Url.create(router.asPath).pathname))

  useEffect(() => {
    const checkUrlChange = () => {
      const currentUrl = Url.sanitizeUrl(Url.create(window.location.pathname).pathname)
      const lastUrlUpdated = Url.sanitizeUrl(lastUrl)
      if (currentUrl !== lastUrlUpdated) {
        setLastUrl(currentUrl)
      }
    }
    checkUrlChange()
    const interval = setInterval(checkUrlChange, 500)
    return () => clearInterval(interval)
  }, [])

  return lastUrl
}

type MicrofrontendProps = {
  folder: string
  projectName: string
  urlBasename: string
}

export const Microfrontend = (props: MicrofrontendProps) => {
  const updatedUrl = useUrlChanges()
  const router = useRouter()
  const [origin, setOrigin] = useState(isSsr ? '' : window.location.origin)
  const [basename] = useState(() =>
    IS_PR_PREVIEW ? Url.join('/', VERSION, props.urlBasename) : `/${props.urlBasename}`
  )
  const [version, setVersion] = useState<string | undefined>(
    (router.query?.['__swh'] as string) || (localStorage.getItem(microFrontEndLocalStorage) as string)
  )
  const [localFile] = useState((): string =>
    version ? `/${props.folder}/${version}/entrypoint.js` : `/${props.folder}/root.js`
  )

  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const cookies = isomorphicCookies.getAll()
    if (cookies.token) window.document.cookie = `token=${cookies.token};domain=${MICROFRONTEND_CDN}`
  }, [router.pathname])

  useEffect(() => {
    if (IS_PRD) return
    const queryStringValue = router.query['__swh']
    if (queryStringValue) {
      localStorage.setItem(microFrontEndLocalStorage, queryStringValue as string)
      return
    }
    const localStorageValue = localStorage.getItem(microFrontEndLocalStorage)
    if (!queryStringValue && localStorageValue) {
      BlackBird.replace(
        {
          pathname: router.pathname,
          query: { ...router.query, __swh: localStorageValue }
        },
        undefined,
        { shallow: true }
      )
    }
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      if (store.subscribers.size > 1) {
        clearInterval(interval)
        setIsLoading(false)
      }
    }, 300)
    return () => clearInterval(interval)
  }, [])

  // On mount: MF container may be new (different Next.js page mounting same MF).
  // The MF's own router handles subsequent in-page navigations — no remount needed.
  useEffect(() => {
    broadcastRerender(props.projectName)
  }, [props.projectName])

  // MF navigated internally (pushState without Next.js knowing): sync URLs.
  useEffect(() => {
    const pathFromNext = Url.sanitizeUrl(Url.create(router.asPath).pathname)
    const browserPath = Url.sanitizeUrl(updatedUrl)
    if (browserPath === pathFromNext) return
    setOrigin(window.location.origin)
    const pushStateUrl = IS_PR_PREVIEW ? Url.join(VERSION, browserPath) : browserPath
    const redirect = new URL(pushStateUrl, window.location.origin)
    redirect.search = window.location.search
    void broadcastRerender(props.projectName)
    history.replaceState({}, '', redirect.href)
  }, [updatedUrl, router.pathname, props.projectName])

  useEffect(() => {
    const handler = (e: unknown) => {
      if (typeof e === 'string') {
        const pathFromNext = Url.sanitizeUrl(Url.create(e).pathname)
        const browserPath = Url.sanitizeUrl(window.location.pathname)
        if (pathFromNext === browserPath) broadcastRerender(props.projectName)
      }
    }
    Router.events.on('routeChangeComplete', handler)
    return () => Router.events.off('routeChangeComplete', handler)
  }, [props.projectName])

  return (
    <Fragment key={`${props.projectName}-container`}>
      {IS_PRD ? null : <VersionSelection version={version} setVersion={setVersion} />}
      {isLoading && (
        <div className="flex w-full min-h-screen items-center justify-center">
          <Spinner size="xlarge" />
        </div>
      )}
      <div
        className={`w-full`}
        data-origin={origin}
        data-basename={basename}
        data-href={router.asPath}
        id={`${props.folder}-container`}
      />
      <Script type="module" src={Url.join(MICROFRONTEND_CDN, localFile)} async crossOrigin="anonymous" />
    </Fragment>
  )
}
